import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map, tap } from 'rxjs/operators';
import { WorkSchedulesPort } from '../../../app/ports/organization/work-schedules.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastV2Service, CoreUIToastDirection } from '../../services/toast.service';
import { OrgSdk, PeopleSdk } from 'lib-klab-sdks';
import { WorkSchedule } from '../../../domain/models/organization/work-schedule.model';
import { PageMeta } from '../../../domain/models/organization/page.model';
import { mapSdkPaginationToDomainPageMeta } from '../../mappers/organization/page.mapper';
import { mapOrganizationErrorToMessage } from '../../utils/organization-error.mapper';
import { mapSDKWorkScheduleToDomain } from '../../mappers/organization/work-schedule.mapper';
import { UpdateWorkScheduleRequest } from '../../../domain/models/organization/request/update-work-schedule.request';
import { CreateWorkScheduleRequest } from '../../../domain/models/organization/request/create-work-schedule.request';
import { workSchedulesSdkMock } from '../../mocks/organization/work-schedules.mock';
import { boldSingleQuotedText } from '../../../shared/utils/text/text-format.utils';
import { CrudMockAction } from '../../config/crud-mock-action.type';
import { EnvMicroOrganization } from '../../config/env.micro.organization';
import { ORG_ENV } from '../../config/micros-env.tokens';

@Injectable({ providedIn: 'root' })
export class WorkSchedulesPortAdapter implements WorkSchedulesPort {
    constructor(
        private workSchedulesApi: OrgSdk.WorkSchedulesService,
        private peopleWorkSchedulesApi: PeopleSdk.PeopleWorkSchedulesService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(ORG_ENV) private env: EnvMicroOrganization,
    ) { }

    getWorkSchedules(page: number, size: number): Observable<{ items: WorkSchedule[]; page: PageMeta }> {
        this.appEvents.loaderVisiblePublish(true, 'work-schedules');

        const source$ = this.shouldMock('getAll')
            ? of(workSchedulesSdkMock.getAllWorkSchedules(page, size)).pipe(delay(360))
            : this.workSchedulesApi.getAllWorkSchedules(page, size);

        return source$.pipe(
            map((resp) => {
                const pageData = resp?.[0];
                return {
                    items: (pageData?.content ?? []).map(mapSDKWorkScheduleToDomain),
                    page: mapSdkPaginationToDomainPageMeta(pageData?.page),
                };
            }),
            catchError((err) => { this.showErrorToast(err); return throwError(() => err); }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'work-schedules')),
        );
    }

    getWorkScheduleById(workScheduleId: number): Observable<WorkSchedule> {
        this.appEvents.loaderVisiblePublish(true, 'workScheduleId-getById');

        const source$ = this.shouldMock('getById')
            ? of(workSchedulesSdkMock.getWorkScheduleById(workScheduleId)).pipe(delay(360))
            : this.workSchedulesApi.getWorkScheduleById(workScheduleId);

        return source$.pipe(
            map((resp) => mapSDKWorkScheduleToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'workScheduleId-getById')),
        );
    }

    createWorkSchedule(payload: CreateWorkScheduleRequest) {
        this.appEvents.loaderVisiblePublish(true, 'work-schedules-create');

        const sdkPayload = this.toSdkCreate(payload);

        const source$ = this.shouldMock('create')
            ? of(workSchedulesSdkMock.createWorkSchedule(sdkPayload)).pipe(delay(360))
            : this.workSchedulesApi.createWorkSchedule(sdkPayload);

        return source$.pipe(
            map((resp: OrgSdk.WorkScheduleResponse) => mapSDKWorkScheduleToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err, 'create');
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'work-schedules-create')),
        );
    }

    updateWorkSchedule(scheduleId: number, payload: UpdateWorkScheduleRequest) {
        this.appEvents.loaderVisiblePublish(true, 'work-schedules-update');

        const sdkPayload = this.toSdkUpdate(payload);

        const source$ = this.shouldMock('update')
            ? of(workSchedulesSdkMock.updateWorkSchedule(scheduleId, sdkPayload)).pipe(delay(360))
            : this.workSchedulesApi.updateWorkSchedule(scheduleId, sdkPayload);

        return source$.pipe(
            map((resp: OrgSdk.WorkScheduleResponse) => mapSDKWorkScheduleToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err, 'update');
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'work-schedules-update')),
        );
    }

    deleteSchedule(scheduleId: number): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'schedule-delete');

        const source$ = this.shouldMock('delete')
            ? of(null).pipe(
                tap(() => workSchedulesSdkMock.deleteWorkScheduleById(scheduleId)),
                delay(360),
                map(() => void 0),
            )
            : this.workSchedulesApi.deleteWorkScheduleById(scheduleId).pipe(map(() => void 0));

        return source$.pipe(
            catchError((err) => {
                this.showErrorToast(err, 'delete');
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'schedule-delete')),
        );
    }

    exportWorkSchedules(format: 'CSV' | 'EXCEL'): Observable<Blob> {
        return this.peopleWorkSchedulesApi.exportWorkSchedules(format);
    }

    private toSdkCreate(
        req: CreateWorkScheduleRequest
    ): OrgSdk.WorkScheduleCreateRequest {
        return {
            departmentId: req.departmentId,
            name: req.name,
            days: req.days.map(d => ({
                dayOfWeek: d.dayOfWeek as OrgSdk.DayOfWeekEnum,
                startTime: d.startTime,
                endTime: d.endTime,
                breakTime: d.breakTime,
                countBreakTime: d.countBreakTime,
            })),
        };
    }

    private toSdkUpdate(
        req: UpdateWorkScheduleRequest
    ): OrgSdk.WorkScheduleUpdateRequest {
        return {
            name: req.name,
            departmentId: req.departmentId,
            status: req.status as OrgSdk.StatusEnum,
            days: req.days.map(d => ({
                workDayId: d.workDayId,
                dayOfWeek: d.dayOfWeek as OrgSdk.DayOfWeekEnum,
                startTime: d.startTime,
                endTime: d.endTime,
                breakTime: d.breakTime,
                countBreakTime: d.countBreakTime,
            })),
        };
    }

    private showErrorToast(
        err: any,
        action: 'create' | 'update' | 'delete' | 'read' = 'read',
    ) {
        if (!this.toast) return;

        const status = (err as any)?.status;
        const body = (err as any)?.error;
        const code = body?.code;
        const description = body?.description ?? body?.message;

        const isKlab409Conflict =
            (action === 'create' || action === 'update') &&
            status === 409 &&
            (
                code === 'KLAB409' ||
                (typeof description === 'string' && /horario\s+activo.*departamento/i.test(description))
            );

        if (isKlab409Conflict) {
            const errorMessage =
                typeof description === 'string' && description.trim().length > 0
                    ? description.trim()
                    : 'No se pudo completar la operacion con el horario.';

            this.toast.show({
                data: {
                    type: 'error',
                    title: 'No se pudo registrar el horario',
                    msg: errorMessage,
                    msgHtml: boldSingleQuotedText(errorMessage),
                    direction: CoreUIToastDirection.topCenter,
                    autoClose: false,
                    closable: true,
                },
            });
            return;
        }

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapOrganizationErrorToMessage(err),
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }

    private shouldMock(action: CrudMockAction): boolean {
        return Boolean(
            this.env?.useMocks ||
            this.env?.mock?.workSchedules?.[action]
        );
    }

}
